"use client";

import { motion } from "framer-motion";

const CAPABILITIES = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
      </svg>
    ),
    title: "Automatic Call Answering",
    description: "Every inbound call is answered immediately — no hold music, no voicemail. Available around the clock, including weekends and public holidays.",
    tag: "Always on",
    tagColor: "text-violet-400 bg-violet-500/10 border-violet-500/20",
    accent: "from-violet-500/20 to-transparent",
    iconColor: "bg-violet-500/10 border-violet-500/20 text-violet-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    ),
    title: "Appointment Booking & Rescheduling",
    description: "Callers book or reschedule appointments directly through the voice agent, synced in real time to your existing scheduling system.",
    tag: "Synced live",
    tagColor: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    accent: "from-cyan-500/20 to-transparent",
    iconColor: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
    title: "Call Routing & Live Transfer",
    description: "Routine calls handled automatically. Urgent or complex situations transferred to a human in real time — without the caller being dropped.",
    tag: "Zero drop",
    tagColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    accent: "from-emerald-500/20 to-transparent",
    iconColor: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
      </svg>
    ),
    title: "Message Capture & Summaries",
    description: "Caller details and purpose of each call recorded and delivered as a structured summary to your team via email or SMS.",
    tag: "To inbox or CRM",
    tagColor: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    accent: "from-amber-500/20 to-transparent",
    iconColor: "bg-amber-500/10 border-amber-500/20 text-amber-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
      </svg>
    ),
    title: "Basic Inquiry Handling",
    description: "Common questions about hours, location, services, and availability answered instantly — without front desk involvement.",
    tag: "Instant answers",
    tagColor: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    accent: "from-blue-500/20 to-transparent",
    iconColor: "bg-blue-500/10 border-blue-500/20 text-blue-400",
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
    title: "Scheduling System Integration",
    description: "Connects directly to your booking platform to read availability and confirm appointments, keeping your calendar accurate.",
    tag: "Plug & play",
    tagColor: "text-pink-400 bg-pink-500/10 border-pink-500/20",
    accent: "from-pink-500/20 to-transparent",
    iconColor: "bg-pink-500/10 border-pink-500/20 text-pink-400",
  },
];

export default function VoiceAgentCapabilities({ heading, items }) {
  // Use passed items if provided, otherwise fall back to built-in CAPABILITIES
  const caps = items
    ? items.map((item, i) => ({ ...CAPABILITIES[i % CAPABILITIES.length], ...item }))
    : CAPABILITIES;

  return (
    <section className="bg-zinc-950 py-24 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-14">
          <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">Capabilities</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-white max-w-lg">
              {heading || "What the system handles"}
            </h2>
            <p className="text-zinc-400 text-base max-w-sm lg:text-right">
              Every scenario your front desk faces — handled automatically.
            </p>
          </div>
        </div>

        {/* Bento-style grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {caps.map((cap, i) => (
            <motion.div
              key={cap.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className="group relative rounded-2xl border border-white/[0.07] bg-zinc-900/60 p-6 overflow-hidden hover:border-white/[0.14] transition-all duration-300"
            >
              {/* Accent glow top-left */}
              <div className={`absolute -top-8 -left-8 w-32 h-32 rounded-full bg-gradient-to-br ${cap.accent} blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

              {/* Icon */}
              <div className={`relative w-10 h-10 rounded-xl border flex items-center justify-center transition-all duration-300 mb-5 ${cap.iconColor || "bg-white/[0.05] border-white/[0.08] text-zinc-400"}`}>
                {cap.icon}
              </div>

              {/* Content */}
              <h3 className="text-sm font-semibold text-white mb-2 leading-snug">
                {cap.title}
              </h3>
              <p className="text-sm text-zinc-500 leading-relaxed mb-5">
                {cap.description}
              </p>

              {/* Tag */}
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cap.tagColor}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {cap.tag}
              </span>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
